import React, { useEffect, useRef, useState } from 'react';
import { toast } from 'react-toastify';
import { Dialog, DialogTitle, DialogContent, DialogActions, Button } from '@mui/material';
import { Panel, IPanelProps } from '../Layout/Panel/Panel';
import { EIcon } from '../Layout/Panel/views/components/Icon';
import { ISegmentProps } from '../Layout/Panel/views/Segment';
import { SummaryPanel, SummaryPanelProps } from '../../pages/Project/SummaryPanel';
import { useAllSubTypesByType, useAllSubTypeValues } from '../../pages/Project/hooks/types';
import { fetchAllSubValuesBySubType } from '../../services/type.service';
import { addSubTypesToProject } from '../../services/project.service';
import { SubTypeValueDataModel } from '../../models/project.model';
import { SubTypeDataModel, SubValueDataModel } from '../../models/types.model';

export const EditSubTypeDialog = (props: any) => {
	// Just alternates to trigger reload
	const [toggleForReload, setToggleForReload] = useState<boolean>(false);

	const [fields, setFields] = useState<any>({});
	const [subTypePreviewMode, setSubTypePreviewMode] = useState<boolean>(false);
	// For the currently selected sub type
	const [subTypeValues, setSubTypeValues] = useState<Array<SubValueDataModel>>([]);
	const [selectedSubType, setSelectedSubType] = useState<SubTypeDataModel>();
	// Dictionary of subtype id to selected subtype-value ids.
	const [selectedSubTypeValues, setSelectedSubTypeValues] = useState<Record<number, number[]>>({});
	const [isHydrating, setIsHydrating] = useState<boolean>(false);
	const [isSaving, setIsSaving] = useState<boolean>(false);
	const [hasUnmatchedSavedValues, setHasUnmatchedSavedValues] = useState<boolean>(false);
	const subTypeValuesRequestId = useRef(0);

	const subTypesModel: Array<SubTypeDataModel> | undefined = useAllSubTypesByType(props?.selectedProjectType?.typeId);

	const subTypeValuesMaster: Array<SubTypeValueDataModel> | undefined = useAllSubTypeValues();

	const onRefreshPanel = () => {
		// Purely for refreshing the page
		setToggleForReload(!toggleForReload);
		props.onRefreshTypePanel();
	};

	const handleInputChange = (event: any) => {
		const { name, value } = event.target;

		// Update the fields state with the new value
		setFields((currentFields: any) => ({
			...currentFields,
			[name]: value,
		}));
	};

	const onSubTypeClick = async (subTypeId: number) => {
		if (subTypeId) {
			const requestId = ++subTypeValuesRequestId.current;
			setIsHydrating(true);
			try {
				const response = await fetchAllSubValuesBySubType('type/subtype/value/getall?subtypeId=' + subTypeId);

				if (requestId !== subTypeValuesRequestId.current) return;
				if (!response.data?.success) {
					toast.error(response.data?.message || 'Unable to load subtype values.');
					return;
				}
				setSubTypeValues(response.data.data);
			} catch (err: any) {
				if (requestId === subTypeValuesRequestId.current) {
					toast.error(err?.response?.data?.message || err?.message || 'Unable to load subtype values.');
				}
			} finally {
				if (requestId === subTypeValuesRequestId.current) setIsHydrating(false);
			}
		}
	};

	const getSubTypes = () => {
		let data: any = [];

		if (subTypesModel) {
			data = subTypesModel.map((subType) => ({
				text: subType.name,
				isTextSelected: selectedSubType?.id === subType.id,
				textClickCb: async () => {
					setSelectedSubType(subType);
					await onSubTypeClick(subType.id);
				},
			}));
		}

		return data;
	};

	const getSubTypeNameFromId = (id: number): string => {
		if (id && subTypesModel) {
			const info = subTypesModel.find((item) => item.id === id);

			return info?.name ? info?.name : '';
		}

		return '';
	};

	const getSubTypeValueFromId = (id: number) => {
		if (id && subTypeValuesMaster) {
			const info = subTypeValuesMaster.find((item) => item.id === id);

			return info?.value ? info?.value : '';
		}

		return '';
	};

	const getSubTypeValueFromIds = (ids: Array<number>) => {
		let data: any = [];

		if (ids) {
			ids.map((id) => data.push(getSubTypeValueFromId(Number(id))));
		}

		return data;
	};

	const subTypeNamesProps: ISegmentProps = {
		header: {
			text: 'Name',
		},
		placeholderText: 'No sub types set',
		data: getSubTypes(),
	};

	const handleClose = () => {
		subTypeValuesRequestId.current += 1;
		props.close();
		setSubTypePreviewMode(false);
		setSelectedSubTypeValues({});
		setSelectedSubType(undefined);
		setSubTypeValues([]);
		setFields({});
		setIsHydrating(false);
		setIsSaving(false);
		setHasUnmatchedSavedValues(false);
	};

	const subTypeValuesProps: ISegmentProps = {
		header: {
			text: 'Value',
		},
		placeholderText: 'No values available',
		data: subTypeValues.map((value) => {
			const valueId = Number(value.id);
			const selectedValues =
				selectedSubType?.id && selectedSubTypeValues[selectedSubType?.id]
					? selectedSubTypeValues[selectedSubType?.id]
					: [];
			return {
				text: value.value,
				icons: [
					{
						icon: EIcon.CHECKBOX,
						inputText: value.value,
						iconCb: () => {
							if (!selectedSubType) return;
							setSelectedSubTypeValues((currentSelections) => {
								const currentValues = currentSelections[selectedSubType.id] || [];
								const nextValues = currentValues.includes(valueId)
									? currentValues.filter((selectedValue) => selectedValue !== valueId)
									: [...currentValues, valueId];
								const nextSelections = { ...currentSelections };

								if (nextValues.length) nextSelections[selectedSubType.id] = nextValues;
								else delete nextSelections[selectedSubType.id];

								return nextSelections;
							});
						},
						// This will update the component whenever state changes
						isIconChecked: selectedValues?.includes(valueId),
					},
				],
			};
		}),
	};

	const subTypePanelProps: IPanelProps = {
		container: {
			height: '250px',
			padding: '2px',
		},
		segments: [subTypeNamesProps, subTypeValuesProps],
	};

	const getSellingPriceFromSubTypeValueIds = (subTypeId: number, valueIds: Array<number>) => {
		return valueIds.map((valueId) => Number(fields[`${subTypeId},${valueId}_sellingPrice`] ?? 0));
	};

	const getPurchaseCostFromSubTypeValueIds = (subTypeId: number, valueIds: Array<number>) => {
		return valueIds.map((valueId) => Number(fields[`${subTypeId},${valueId}_purchaseCost`] ?? 0));
	};

	const summaryPanelProps: SummaryPanelProps = {
		containerHeight: '250px',
		items: Object.keys(selectedSubTypeValues).map((key) => {
			const props = {
				subtypeId: Number(key),
				name: getSubTypeNameFromId(Number(key)),
				values: getSubTypeValueFromIds(selectedSubTypeValues[key]),
				valuesId: selectedSubTypeValues[key],
				sellingPriceList: getSellingPriceFromSubTypeValueIds(Number(key), selectedSubTypeValues[key]),
				purchaseCostList: getPurchaseCostFromSubTypeValueIds(Number(key), selectedSubTypeValues[key]),
			};
			return props;
		}),
		handleInputChange: handleInputChange,
	};

	const handleSubmit = async () => {
		if (!Object.values(selectedSubTypeValues).some((values) => values.length)) {
			toast.error('Select at least one subtype value before saving.');
			return;
		}

		setIsSaving(true);
		try {
			let payload: any = [];
			// Iterate through selected values to get the sellingPrice from fields
			Object.keys(selectedSubTypeValues).forEach((subTypeId: any) => {
				selectedSubTypeValues[subTypeId].map((selectedSubTypeValueId: number) => {
					const key = subTypeId + ',' + selectedSubTypeValueId;
					let oldSubtypeIndex = payload.findIndex((subTypeInfo: any) => subTypeInfo.subTypeId === Number(subTypeId));
					if (oldSubtypeIndex >= 0) {
						payload[oldSubtypeIndex].subTypeValues.push({
							valueId: selectedSubTypeValueId,
							sellingPrice: Number(fields[key + '_sellingPrice'] ?? 0),
							purchaseCost: Number(fields[key + '_purchaseCost'] ?? 0),
						});
					} else {
						payload.push({
							subTypeId: Number(subTypeId),
							subTypeValues: [
								{
									valueId: selectedSubTypeValueId,
									sellingPrice: Number(fields[key + '_sellingPrice'] ?? 0),
									purchaseCost: Number(fields[key + '_purchaseCost'] ?? 0),
								},
							],
						});
					}
				});
			});
			const result = await addSubTypesToProject(
				'project/addSubTypeToType?projectToTypeId=' + props.selectedProjectType.id,
				{ data: payload }
			);

			if (result.data.success) {
				toast.success(result.data.message);
				onRefreshPanel();
				handleClose();
			} else {
				toast.error(result.data.message);
			}
		} catch (err: any) {
			toast.error(err?.response?.data?.message || err?.message);
		} finally {
			setIsSaving(false);
		}
	};

	useEffect(() => {
		if (!props.show || !subTypeValuesMaster) return;

		const savedSubtypes = props.selectedProjectType?.subTypes || [];
		const selections: Record<number, number[]> = {};
		const initialFields: Record<string, number> = {};
		let hasUnmatchedValues = false;
		savedSubtypes.forEach((subtype: any) => {
			const subTypeId = Number(subtype.subTypeId);
			const values = subtype.subtypeValuesList || [];
			values.forEach((value: any) => {
				const savedValueId = Number(value.id);
				const savedCatalogValue = subTypeValuesMaster.find((catalogValue) => Number(catalogValue.id) === savedValueId);
				const savedIdBelongsToSubtype =
					savedCatalogValue &&
					(savedCatalogValue.subtypeId === undefined || Number(savedCatalogValue.subtypeId) === subTypeId);
				const matchingValues = subTypeValuesMaster.filter(
					(catalogValue) =>
						Number(catalogValue.subtypeId) === subTypeId &&
						catalogValue.value.trim().toLowerCase() ===
							String(value.value || '')
								.trim()
								.toLowerCase()
				);
				const effectiveValueId = savedIdBelongsToSubtype
					? savedValueId
					: matchingValues.length === 1
					? Number(matchingValues[0].id)
					: null;

				if (effectiveValueId === null) {
					hasUnmatchedValues = true;
					return;
				}
				selections[subTypeId] = [...(selections[subTypeId] || []), effectiveValueId];
				initialFields[`${subTypeId},${effectiveValueId}_sellingPrice`] = Number(value.sellingPrice ?? 0);
				initialFields[`${subTypeId},${effectiveValueId}_purchaseCost`] = Number(value.purchaseCost ?? 0);
			});
		});

		setSubTypePreviewMode(false);
		setSelectedSubTypeValues(selections);
		setFields(initialFields);
		setHasUnmatchedSavedValues(hasUnmatchedValues);
	}, [props.show, props.selectedProjectType, subTypeValuesMaster]);

	useEffect(() => {
		if (!props.show) return;

		setSubTypeValues([]);
		setSelectedSubType(undefined);
		if (!subTypesModel || !subTypeValuesMaster) {
			setIsHydrating(true);
			return;
		}
		if (subTypesModel.length) {
			const firstSubType = subTypesModel[0];
			setSelectedSubType(firstSubType);
			onSubTypeClick(firstSubType.id);
		} else {
			setIsHydrating(false);
		}
	}, [props.show, props.selectedProjectType?.id, subTypesModel, subTypeValuesMaster]);

	const handleNext = () => {
		setSubTypePreviewMode((prevState) => !prevState);
	};

	const hasSelections = Object.values(selectedSubTypeValues).some((values) => values.length > 0);

	return (
		<Dialog open={props.show} onClose={handleClose} aria-labelledby='customized-dialog-title'>
			<DialogTitle>{!subTypePreviewMode ? 'Edit SubType' : 'Confirmation'}</DialogTitle>
			<DialogContent style={{ width: '500px' }}>
				{hasUnmatchedSavedValues && (
					<div role='alert' style={{ color: '#d32f2f', paddingBottom: '12px' }}>
						Some saved values could not be matched to their subtype. Review the subtype catalogue before saving.
					</div>
				)}
				{isHydrating ? (
					<div style={{ padding: '24px', textAlign: 'center' }}>Loading subtype configuration…</div>
				) : !subTypePreviewMode ? (
					<Panel panelProps={subTypePanelProps}></Panel>
				) : (
					<SummaryPanel panelProps={summaryPanelProps}></SummaryPanel>
				)}
			</DialogContent>
			<DialogActions>
				{!subTypePreviewMode && (
					<Button variant='outlined' size='small' onClick={handleClose}>
						Cancel
					</Button>
				)}
				<Button
					variant='contained'
					size='small'
					onClick={handleNext}
					disabled={!subTypePreviewMode && (isHydrating || !hasSelections || hasUnmatchedSavedValues)}>
					{!subTypePreviewMode ? 'Next' : 'Back'}
				</Button>
				{subTypePreviewMode && (
					<Button type='submit' size='small' variant='contained' onClick={handleSubmit} disabled={isSaving}>
						{'Confirm'}
					</Button>
				)}
			</DialogActions>
		</Dialog>
	);
};
